// src/components/schedules/SchedulesPlanning.tsx
import { useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Power, PowerOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useReportScheduling,
  useCreateReportScheduling,
  useUpdateReportScheduling,
  useDeleteReportScheduling,
  useReportList,
} from "@/hooks/useReportDelivery";
import { ReportScheduling } from "@/types/reportDelivery";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

const DAYS_LABELS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const DAYS_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export function SchedulesPlanning() {
  const { data: schedulings, isLoading: schedulingsLoading } = useReportScheduling();
  const { data: reports, isLoading: reportsLoading } = useReportList({ active: true });
  const createScheduling = useCreateReportScheduling();
  const updateScheduling = useUpdateReportScheduling();
  const deleteScheduling = useDeleteReportScheduling();

  const [showDialog, setShowDialog] = useState(false);
  const [editingScheduling, setEditingScheduling] = useState<ReportScheduling | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    reportID: "",
    send_hour: "8",
    days: [] as number[],
    enabled: true,
  });

  const handleOpenDialog = (scheduling?: ReportScheduling) => {
    if (scheduling) {
      setEditingScheduling(scheduling);
      const daysArray = scheduling.days
        ? scheduling.days.split(",").map((d) => parseInt(d.trim()))
        : [];
      setFormData({
        reportID: scheduling.reportID.toString(),
        send_hour: scheduling.send_hour.toString(),
        days: daysArray,
        enabled: scheduling.enabled === 1,
      });
    } else {
      setEditingScheduling(null);
      setFormData({
        reportID: "",
        send_hour: "8",
        days: [],
        enabled: true,
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        reportID: parseInt(formData.reportID),
        send_hour: parseInt(formData.send_hour),
        days: formData.days.length > 0 ? formData.days.sort().join(",") : undefined,
        enabled: formData.enabled,
      };

      if (editingScheduling) {
        await updateScheduling.mutateAsync({
          id: editingScheduling.id,
          data: payload,
        });
      } else {
        await createScheduling.mutateAsync(payload);
      }
      setShowDialog(false);
    } catch {
      // Toast géré dans le hook
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteScheduling.mutateAsync(id);
      setDeleteConfirm(null);
    } catch {
      // Toast géré dans le hook
    }
  };

  const handleToggleEnabled = async (scheduling: ReportScheduling) => {
    try {
      await updateScheduling.mutateAsync({
        id: scheduling.id,
        data: { enabled: scheduling.enabled === 1 ? false : true },
      });
    } catch {
      // Toast géré dans le hook
    }
  };

  const toggleDay = (day: number) => {
    setFormData((prev) => ({
      ...prev,
      days: prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day],
    }));
  };

  const isLoading = schedulingsLoading || reportsLoading;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Planning des envois</CardTitle>
            <CardDescription>
              Programmez l'envoi automatique de vos rapports par heure et jours de la semaine
            </CardDescription>
          </div>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="mr-2 h-4 w-4" />
            Nouveau planning
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Chargement...</div>
        ) : !schedulings || schedulings.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Aucun planning configuré. Créez-en un pour commencer.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rapport</TableHead>
                <TableHead>Heure d'envoi</TableHead>
                <TableHead>Jours</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedulings.map((scheduling) => {
                const daysArray = scheduling.days
                  ? scheduling.days.split(",").map((d) => parseInt(d.trim()))
                  : null;
                const daysLabel = daysArray
                  ? daysArray.map((d) => DAYS_SHORT[d]).join(", ")
                  : "Tous les jours";

                return (
                  <TableRow key={scheduling.id}>
                    <TableCell className="font-medium">{scheduling.report_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        {scheduling.send_hour.toString().padStart(2, "0")}:00
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{daysLabel}</span>
                    </TableCell>
                    <TableCell>
                      {scheduling.enabled === 1 ? (
                        <Badge variant="default" className="bg-green-500">
                          <Power className="mr-1 h-3 w-3" />
                          Actif
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <PowerOff className="mr-1 h-3 w-3" />
                          Désactivé
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleEnabled(scheduling)}
                          disabled={updateScheduling.isPending}
                        >
                          {updateScheduling.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : scheduling.enabled === 1 ? (
                            <PowerOff className="h-4 w-4" />
                          ) : (
                            <Power className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(scheduling)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteConfirm(scheduling.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Dialog Créer/Éditer */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingScheduling ? "Modifier le planning" : "Nouveau planning"}
            </DialogTitle>
            <DialogDescription>
              Configurez l'heure et les jours d'envoi. Si aucun jour n'est sélectionné, l'envoi
              sera quotidien.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reportID">Rapport *</Label>
                <Select
                  value={formData.reportID}
                  onValueChange={(value) => setFormData({ ...formData, reportID: value })}
                  required
                  disabled={!!editingScheduling}
                >
                  <SelectTrigger id="reportID">
                    <SelectValue placeholder="Sélectionnez un rapport" />
                  </SelectTrigger>
                  <SelectContent>
                    {reports?.map((report) => (
                      <SelectItem key={report.reportID} value={report.reportID.toString()}>
                        {report.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="send_hour">Heure d'envoi (locale serveur) *</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="send_hour"
                    type="number"
                    min="0"
                    max="23"
                    value={formData.send_hour}
                    onChange={(e) => setFormData({ ...formData, send_hour: e.target.value })}
                    required
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">
                    {formData.send_hour.padStart(2, "0")}:00
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Heure locale du serveur (0-23). Ex: 20 = 8h du soir.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Jours de la semaine (optionnel)</Label>
                <div className="grid grid-cols-2 gap-3">
                  {DAYS_LABELS.map((label, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Checkbox
                        id={`day-${index}`}
                        checked={formData.days.includes(index)}
                        onCheckedChange={() => toggleDay(index)}
                      />
                      <Label
                        htmlFor={`day-${index}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {label}
                      </Label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Si aucun jour n'est sélectionné, le rapport sera envoyé tous les jours.
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="enabled"
                  checked={formData.enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                />
                <Label htmlFor="enabled">Planning activé</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={createScheduling.isPending || updateScheduling.isPending}
              >
                {createScheduling.isPending || updateScheduling.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  "Enregistrer"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmation suppression */}
      <AlertDialog open={deleteConfirm !== null} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce planning ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

